package dev.akshaychordiya.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.akshaychordiya.MetaTextStyle
import dev.akshaychordiya.content.Newsletter
import dev.akshaychordiya.fadeUp
import dev.akshaychordiya.smoothTransition
import dev.akshaychordiya.toSitePalette
import org.jetbrains.compose.web.attributes.FormMethod
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.action
import org.jetbrains.compose.web.attributes.method
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.required
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input

/** Same tinted-panel treatment as the 1:1 callout, so the two read as siblings. */
val SubscribeBoxStyle = CssStyle.base {
    val brand = colorMode.toSitePalette().brandPrimary.toRgb()
    Modifier
        .fillMaxWidth()
        .padding(1.5.cssRem)
        .borderRadius(0.9.cssRem)
        .backgroundColor(brand.copyf(alpha = if (colorMode.isLight) 0.07f else 0.10f))
        .border(1.px, LineStyle.Solid, brand.copyf(alpha = 0.30f))
        .gap(0.6.cssRem)
}

/** Stacks on a phone, single row from tablet up. */
val SubscribeFormStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(top = 0.5.cssRem)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(0.5.cssRem)
    }
    Breakpoint.SM { Modifier.flexDirection(FlexDirection.Row) }
}

val SubscribeInputStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 0.6.cssRem, leftRight = 0.9.cssRem)
            .borderRadius(0.5.cssRem)
            .fontSize(0.95.cssRem)
            .backgroundColor(colorMode.toSitePalette().background)
            .color(colorMode.toSitePalette().text)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .smoothTransition("border-color")
    }
    focusVisible { Modifier.border(1.px, LineStyle.Solid, colorMode.toSitePalette().brandPrimary) }
}

val SubscribeButtonStyle = CssStyle {
    base {
        Modifier
            .padding(topBottom = 0.6.cssRem, leftRight = 1.2.cssRem)
            .borderRadius(0.5.cssRem)
            .fontSize(0.95.cssRem)
            .fontWeight(600)
            .cursor(Cursor.Pointer)
            .whiteSpace(WhiteSpace.NoWrap)
            .backgroundColor(colorMode.toSitePalette().brandPrimary)
            .color(colorMode.toSitePalette().background)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().brandPrimary)
            .smoothTransition("transform", "box-shadow")
    }
    hover {
        Modifier
            .translateY((-2).px)
            .boxShadow(
                offsetY = 6.px,
                blurRadius = 16.px,
                color = colorMode.toSitePalette().brandPrimary.toRgb().copyf(alpha = 0.35f),
            )
    }
}

/**
 * Visually hidden, but still focusable and still submitted - which is exactly what a honeypot needs.
 *
 * `display: none` would be wrong: plenty of bots skip hidden inputs, and some browsers won't submit them. Pushing it
 * out of the viewport keeps it in the form for anything filling fields blindly, while `tabindex="-1"` and
 * `aria-hidden` keep it away from keyboard and screen-reader users.
 */
val HoneypotStyle = CssStyle.base {
    Modifier
        .position(Position.Absolute)
        .left((-9999).px)
        .width(1.px)
        .height(1.px)
        .opacity(0)
        .styleModifier { property("pointer-events", "none") }
}

/**
 * Email signup.
 *
 * A real `<form>` with a real `action`, submitted by the browser - no JavaScript involved. On a statically exported
 * page whose Kotlin/JS bundle is a few hundred KB, that means the form works the instant the HTML lands rather than
 * once the bundle has hydrated, and it keeps working if the bundle fails outright. The endpoint answers with a styled
 * confirmation page on this same site, so the hand-off doesn't feel like leaving.
 */
@Composable
fun Subscribe(
    headline: String = "Get new articles by email",
    pitch: String = "New articles, talks and book news, once in a while. No spam, and unsubscribe whenever you like.",
) {
    val palette = ColorMode.current.toSitePalette()

    Column(SubscribeBoxStyle.toModifier().fadeUp()) {
        SpanText(headline, Modifier.fontSize(1.15.cssRem).fontWeight(700))
        SpanText(pitch, Modifier.fontSize(0.95.cssRem).color(palette.subdued))

        Form(attrs = SubscribeFormStyle.toModifier().toAttrs {
            action(Newsletter.FORM_ACTION)
            method(FormMethod.Post)
        }) {
            Input(
                type = InputType.Email,
                attrs = SubscribeInputStyle.toModifier().toAttrs {
                    name(Newsletter.EMAIL_FIELD)
                    placeholder("you@example.com")
                    required()
                    attr("autocomplete", "email")
                    attr("aria-label", "Your email address")
                },
            )
            // The honeypot. Bots fill it in; people never see it.
            Input(
                type = InputType.Text,
                attrs = HoneypotStyle.toModifier().toAttrs {
                    name(Newsletter.HONEYPOT_FIELD)
                    attr("tabindex", "-1")
                    attr("aria-hidden", "true")
                    attr("autocomplete", "off")
                },
            )
            Input(
                type = InputType.Submit,
                attrs = SubscribeButtonStyle.toModifier().toAttrs { attr("value", "Subscribe") },
            )
        }
    }
}
